/** @jsx jsx */
import React, { useState } from "react"
import { Link, navigate } from "gatsby"
import styled from "@emotion/styled"

import CapitariaLogoBlack from "../ui/images/capitaria-logo-black"

import { useQueryString } from "@capitaria/capitaria-utms"
import { trackEvent } from "../../helpers/tracker"

import { jsx, css } from "@emotion/react"
import { replaceMcOrigenWithValueABTesting } from "../../helpers/replace-param-query-string"
import { useFeatureIsOn } from "@growthbook/growthbook-react"


const Anchor = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  letter-spacing: 0.8px;
  line-height: 12px;
  text-align: center;
  color: #000000;
  border-radius: 6px;
  text-decoration: none;
  padding: 0.75rem 0;
  max-width: 130px;
  font-family: Inter;
  cursor: pointer;
  width: 100%;

  &:hover {
    transition: 0.3s;
    color: #43b649;
  }

  @media (max-width: 926px) {
    min-width: 235px;
    margin-left: 0;
    margin-bottom: 1rem;
  }
`

const dropdownLinkCss = css`
  @media (max-width: 1200px) {
    padding: 0.45rem 0;
    min-width: 100px;
    margin-left: 1px;
  }
`

const primaryCSS = css`
  color: #ffffff;
  background: #43b649;
  padding: calc(0.75rem + 1px) 0;
  border: 0;
  border-radius: 20px;
  width: 150px;
  height: 35px;
  font-size: 12px;
  letter-spacing: 0.6px;
  margin-left: 2.75rem;
  &:hover {
    transition: 0.3s;
    background: #ffffff;
    border: 2px solid #43b649;
    color: #43b649;
  }
  @media (max-width: 926px) {
    margin-left: 0;
  }
`

type Props = {
  href: string
  text: string
  styles?: any
  target?: string
  primary?: boolean
  className?: string
  onClick?: (event: React.MouseEvent) => void
}

const CustomAnchor = ({
  href,
  text,
  styles = null,
  target,
  primary = false,
  className = "",
  onClick,
}: Props) => {
  const primaryStyles = primary ? primaryCSS : null

  return (
    <Anchor
      href={href}
      css={[primaryStyles, styles]}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : ""}
      className={className}
      onClick={onClick}
    >
      {text}
    </Anchor>
  )
}

const DesktopContainer = styled.div`
  position: fixed;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  padding: 1rem 2rem;
  width: 100%;
  height: 122px;

  @media (max-width: 926px) {
    display: none;
  }
`

const MobileContainer = styled.div`
  background-color: #ffffff;
  width: 100%;
  display: none;

  @media (max-width: 926px) {
    display: block;
  }
`

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
`

const ToggleButton = styled.div`
  display: flex;
  align-items: center;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: #1f2425;
  text-decoration: none;
  padding-right: 0.25rem;
  min-height: 22px;
`

const LinkList = styled.ul`
  list-style: none;
  display: flex;
  list-style-type: none;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
  width: 100%;

  @media (max-width: 926px) {
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    padding-top: 2rem;
    border-top: 1px solid #ffffff;
  }
`

const Item = styled.li`
  text-align: center;
  cursor: pointer;
  position: relative;
  width: 100%;
  @media (max-width: 926px) {
    width: auto;
  }
  > ul {
    list-style: none;
    opacity: 0;
    -webkit-transition: all 0.3s ease-in-out;
    -moz-transition: all 0.3s ease-in-out;
    -ms-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
    margin: 0;
    padding: 0;
    display: none;
    @media (min-width: 950px) {
      position: absolute;
    }
    @media (max-width: 926px) {
      top: 0;
      left: 120px;
    }
    @media (max-width: 580px) {
      top: 0;
      left: 168px;
    }

    > li {
      padding: 0;
      float: left;
      display: block;
      border: 1px solid #ededef;
      background-color: #ffffff;
      @media (max-width: 926px) {
        padding: 5px;
        > a {
          margin: 0;
        }
      }
    }
  }
  &:hover {
    > ul {
      display: flex;
      flex-direction: column;
      opacity: 1;
      box-shadow: 4px 5px 17px 6px #ededef;
      min-width: 126px;
    }
  }
`

const scrollToTop = (parsedQueryString: any, newHome: boolean = false) => (event?: React.MouseEvent) => {
  if (event?.preventDefault)
    event.preventDefault()
  if (location.pathname !== '/') {
    navigate(`/${replaceMcOrigenWithValueABTesting(parsedQueryString, newHome)}`)
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

}

type LinkType = {
  text: string
  path: string
  target?: string
  className?: string
  primary?: boolean
  onClick?: (event?: React.MouseEvent) => void
  dropdownLinks?: LinkType[]
  newHome?: boolean
}

const links: LinkType[] = [
  {
    text: "Inicio",
    path: "/",
    onClick: scrollToTop
  },
  {
    text: "Mercados",
    target: "_self",
    path: "/aspectos-operativos",
  },
  {
    text: "Plataforma",
    path: "",
    onClick: () => {
      trackEvent("navbar-v2:platform")
    },
    dropdownLinks: [
      {
        text: "Crear Demo",
        path: "/crear-demo",
        target: "_self",
      },
      {
        text: "MT5 Chile ",
        path: "/mt5-capitaria.html",
      },
      {
        text: "MT5 Mundo",
        path: "/mt5-ktgroup.html",
      },
    ],
  },
  {
    text: "Educación",
    path: "",
    onClick: () => {
      trackEvent("navbar-v2:education")
    },
    dropdownLinks: [
      {
        text: "Cursos",
        path: "https://www.capitariacursostrading.com",
      },
      {
        text: "Blog",
        path: "https://blog.capitaria.com/",
      },
    ],
  },
  {
    text: "Quiénes somos",
    target: "_self",
    path: "/acerca-de-nosotros",
  },
  {
    text: "Empezar",
    path: "https://onboarding.capitaria.com/sign-up",
    className: "gtm-btn-cuenta-0",
    primary: true,
    onClick: () => {
      trackEvent("navbar-v2:sign-up")
    }
  },
]

const NavbarItem = ({
  text,
  path,
  target,
  primary,
  className,
  onClick,
  dropdownLinks,
  newHome = false
}: LinkType) => {

  const {
    parsedQueryString,
  } = useQueryString()

  let defaultOnClick = onClick
  if (path === "/" && onClick) {
    defaultOnClick = scrollToTop(parsedQueryString, newHome)
  }

  return (
    <Item>
      <CustomAnchor
        className={className}
        href={`${path}${replaceMcOrigenWithValueABTesting(parsedQueryString, newHome)}`}
        target={target ? target : "_blank"}
        text={text}
        primary={primary}
        onClick={defaultOnClick}
      />
      {dropdownLinks && (
        <ul>
          {dropdownLinks.map((link, index) => (
            <li key={`dropdown-link-${index}`}>
              <CustomAnchor
                className={link.className}
                styles={dropdownLinkCss}
                href={link.dropdownLinks ? `${link.dropdownLinks[index]}` : `${link.path}${replaceMcOrigenWithValueABTesting(parsedQueryString, newHome)}`}
                target={link.target ? link.target : "_blank"}
                text={link.text}
              />
            </li>
          ))}
        </ul>
      )}
    </Item>
  )
}

const onClickMenu = (linkProps: LinkType) => (event?: React.MouseEvent) => {
  if (linkProps.onClick && event?.preventDefault && !linkProps.primary)
    event.preventDefault()

  if (linkProps.onClick) {
    linkProps.onClick()
  }
}

const WrapperDesktopContainer = styled.div`
  width: 100%;
  max-width: 980px;
  display: flex;
  flex-direction: row;
  align-items: center;
`

type NavigationProps = {
  newHome: boolean
}


const DesktopNavigation = ({ newHome }: NavigationProps) => {
  const { parsedQueryString } = useQueryString()

  return (
    <DesktopContainer>
      <WrapperDesktopContainer>
        <Link to={`/${replaceMcOrigenWithValueABTesting(parsedQueryString, newHome)}`}>
          <CapitariaLogoBlack />
        </Link>

        <LinkList>
          {links.map((linkProps, idx) => (
            <NavbarItem
              key={`nav-link-${idx}`}
              {...linkProps}
              newHome={newHome}
              onClick={onClickMenu(linkProps)}
            />
          ))}
        </LinkList>
      </WrapperDesktopContainer>
    </DesktopContainer>
  )
}

const MobileNavigation = ({ newHome }: NavigationProps) => {
  const [isOpened, setIsOpened] = useState(false)
  const { parsedQueryString } = useQueryString()

  return (
    <MobileContainer>
      <MobileHeader>
        <Link to={`/${replaceMcOrigenWithValueABTesting(parsedQueryString, newHome)}`}>
          <CapitariaLogoBlack />
        </Link>

        <ToggleButton
          className={`black ${isOpened ? "hamburger active" : "hamburger"}`}
          onClick={() => setIsOpened(!isOpened)}
        >
          <span className="line line-1"></span>
          <span className="line line-2"></span>
          <span className="line line-3"></span>
        </ToggleButton>
      </MobileHeader>

      {isOpened && (
        <LinkList>
          {links.map((linkProps, idx) => (
            <NavbarItem
              key={`nav-link-${idx}`}
              {...linkProps}
              newHome={newHome}
              onClick={onClickMenu(linkProps)}
            />
          ))}
        </LinkList>
      )}
    </MobileContainer>
  )
}

const NavbarUpdate = () => {
  const newHome = useFeatureIsOn("new-home")

  return (
    <>
      <DesktopNavigation newHome={newHome} />
      <MobileNavigation newHome={newHome} />
    </>
  )
}

export default NavbarUpdate
